import { Crumb } from "components/Breadcrumb";
import { FC } from "react";
import {
    BlogPosting,
    BreadcrumbList,
    WithContext,
    Product,
    WebSite,
    VideoObject,
    Organization,
    ListItem
} from "schema-dts";

interface ArticleProps {
    headline: string;
    image: string;
    datePublished: string;
    author?: string;
}

interface BreadCrumbProps {
    trail: Crumb[];
}

interface ProductProps {
    model: string;
    brand: string;
    description: string;
    image?: string;
    price?: number;
    url: string;
}

interface StructuredDataProps {
    type: string;
}

interface VideoProps {
    name: string;
    description: string;
    thumbnailUrl: string;
    uploadDate: string;
    contentUrl: string;
}

/**
 * Generates a JSON object with Article structuredData to be eligible for Googles Rich Results.
 */
export const ArticleStructuredData: FC<ArticleProps> = ({
    headline,
    image,
    datePublished,
    author
}): JSX.Element => {
    const blogPosting: WithContext<BlogPosting> = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": `${headline}`,
        "image": `${image}`,
        "datePublished": `${datePublished}`,
        "dateModified": `${datePublished}`,
        "author": {
            "@type": "Person",
            "name": `${author}`
        }
    };
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(blogPosting) }}
        />
    );
};

/**
 * Generates a JSON object with breadcrumb structuredData to be eligible for Googles Rich Results.
 */
export const BreadCrumbStructuredData: FC<BreadCrumbProps> = ({ trail }): JSX.Element => {
    const breadCrumbPath: ListItem[] = [];

    trail.forEach((x, i) => {
        if (x.mask !== undefined) {
            breadCrumbPath.push({
                "@type": "ListItem",
                "position": i + 1,
                "name": x.title,
                "item": `https://www.autotrack.nl${x.mask}`
            });
        }
    });

    const breadcrumbJson: WithContext<BreadcrumbList> = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadCrumbPath
    };

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJson) }}
        />
    );
};

/**
 * Generates a JSON object with Product structuredData to be eligible for Googles Rich Results. Only to be used on
 * product-pages with a single product, so not on listers.
 */
export const ProductStructuredData: FC<ProductProps> = ({
    model,
    brand,
    description,
    image,
    price,
    url
}): JSX.Element => {
    /*
     * As Multi-Typed Entities are out of scope of the schema-dts package, there is an ignore regarding
     * the Car-type in the ProductStructuredData. Additional Vehicle structuredData is ommitted as this
     * doesn't add any value at the moment.
     */
    const product: WithContext<Product> = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": `${brand} ${model}`,
        "image": `${image}`,
        "description": `${description}`,
        "brand": {
            "@type": "Brand",
            "name": `${brand}`
        },
        "offers": {
            "@type": "Offer",
            "url": `${url}`,
            "priceCurrency": "EUR",
            "price": `${price}`,
            "availability": "https://schema.org/InStock"
        },
        // @ts-ignore
        "car": {
            "@type": "Car"
        }
    };
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(product) }}
        />
    );
};

/*
 * As User Generated Input Types are out of scope of the schema-dts package, there is an ignore regarding
 * the query-input in the SitelinkSearchboxStructuredData.
 */
const sitelinkSearchbox: WithContext<WebSite> = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": "https://www.autotrack.nl/",
    "potentialAction": {
        "@type": "SearchAction",
        "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://www.autotrack.nl/aanbod?trefwoord={query}"
        },
        // @ts-ignore
        "query-input": "required name=query"
    }
};

const logo: WithContext<Organization> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "url": "https://www.autotrack.nl/",
    "logo": "https://www.autotrack.nl/static/images/AutoTrack.png"
};

/**
 * Generates a JSON object based on a passed in StructuredData-type to, either, be eligible:
 * - type "Organization":for Googles sitelink searchbox (searching for cars from Google on Autotrack) Only to be used on the Homepage of the app;
 * - type "WebSite": Mark the logo in the header with structured data;
 */
export const StructuredData: FC<StructuredDataProps> = ({ type }): JSX.Element => {
    const data = type === "WebSite" ? sitelinkSearchbox : logo;
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
    );
};

/**
 * Generates a JSON object to be eligible for Googles Video Markup.
 */
export const VideoStructuredData: FC<VideoProps> = ({
    name,
    description,
    thumbnailUrl,
    uploadDate,
    contentUrl
}): JSX.Element => {
    const video: WithContext<VideoObject> = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": `${name}`,
        "description": `${description}`,
        "thumbnailUrl": `${thumbnailUrl}`,
        "uploadDate": `${uploadDate}`,
        "contentUrl": `${contentUrl}`
    };
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(video) }}
        />
    );
};
